import { fetchApi } from '../client';
import { buildQS } from '../helpers';
import type { Patient, Visit } from '../../../types';
import type { PagedResponse } from '../../../types/pagination';

export interface PatientPagedResponse {
  content: Patient[];
  totalElements: number;
  totalPages: number;
  page: number;
  size: number;
  hasNext: boolean;
  hasPrevious: boolean;
}

export type PatientScope = 'all' | 'own' | 'group' | 'transferred';

export interface PatientListParams {
  page?: number;
  size?: number;
  search?: string;
  gender?: string;
  sortBy?: string;
  sortDir?: string;
  patientScope?: PatientScope;
}

export interface PatientOverviewSafetyAlert {
  type: 'ALLERGY' | 'CONDITION' | 'VITAL' | 'LAB';
  message: string;
  severity: 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW';
}

export interface PatientOverviewVitalReading {
  systolic?: number;
  diastolic?: number;
  pulse?: number;
  temperature?: number;
  oxygenSaturation?: number;
  weight?: number;
  bmi?: number;
  respiratoryRate?: number;
  recordedAt?: string;
  /** Store the row came from (medopd-docs 136 round 2). */
  source?: 'VISIT' | 'WARD' | 'INTAKE' | 'PATIENT_REPORTED' | 'READING' | string;
  /** Readings recorded with this row that have no column ("Pain score 4 /10"). */
  otherReadings?: string[];
}

export interface PatientOverviewCurrentMedication {
  name: string;
  dosage?: string;
  frequency?: string;
  duration?: string;
  prescribedDate?: string;
}

export interface PatientOverviewVisit {
  visitId: string;
  visitDate: string;
  chiefComplaint?: string;
  doctorName?: string;
}

export interface PatientOverviewMedicationItem {
  name: string;
  dosage?: string;
  frequency?: string;
  duration?: string;
  instructions?: string;
}

export interface PatientOverviewPrescription {
  id: string;
  createdAt: string;
  diagnosis?: string;
  status: string;
  doctorName?: string;
  medicationCount: number;
  medicationSummary: string[];
  medications: PatientOverviewMedicationItem[];
}

/** One row of GET /patients/{id}/lab-orders: an order, its report files and its structured result values. */
export interface PatientLabOrderRow {
  id: string;
  testName: string;
  status: string;
  orderedAt?: string;
  prescriptionId?: string | null;
  instructions?: string | null;
  documents?: Array<{ id: string; fileName: string; filePath: string; processingStatus?: string }>;
  parameterValues?: Array<{
    parameterId?: string | number | null;
    parameterName?: string;
    resultValue?: string;
    unit?: string | null;
    referenceRange?: string | null;
    isCritical?: boolean;
    severity?: string | null;
  }>;
}

export interface PatientOverviewLabOrder {
  id: string;
  testName: string;
  status: string;
  createdAt: string;
  isAbnormal: boolean;
  /** Names the critical parameters, e.g. "Critical: Haemoglobin, Platelets". Null when none are. */
  criticalNote?: string | null;
  /**
   * Groups tests ordered in the same requisition. Tests ordered together share this, so the
   * clinician list renders one row per requisition instead of one per test.
   *
   * Null for legacy orders and for clinics with no in-house lab — those stand alone rather than
   * being merged into a single meaningless group.
   */
  accessionId?: string | null;
  accessionNumber?: string | null;
  /** Catalogue category — "Imaging", "Radiology", "Hematology"… Null when the name matches none. */
  category?: string | null;
  /**
   * True when this order is an imaging study rather than bloodwork.
   *
   * Imaging is not a separate order type in ClinVea: an ultrasound is a lab test definition whose
   * category is Imaging, ordered down the same pipeline. This flag is what lets the clinician view
   * separate the two.
   */
  imaging?: boolean;
}

export interface PatientOverviewFollowUp {
  id: string;
  followUpDate: string;
  status: string;
  instructions?: string;
}

export interface PatientOverviewFollowUpSummary {
  pending: PatientOverviewFollowUp[];
  missed: PatientOverviewFollowUp[];
  nextUpcoming?: PatientOverviewFollowUp;
  totalPending: number;
  totalMissed: number;
}

/** GET /patients/{id}/last-visit — LastVisitService.Summary. */
export interface LastVisitSummary {
  visitId: string;
  visitDate: string;
  daysAgo: number;
  doctorName?: string | null;
  consultationMode: 'IN_PERSON' | 'VIDEO';
  chiefComplaint?: string | null;
  duration?: string | null;
  vitals?: { systolic?: number | null; diastolic?: number | null; pulse?: number | null; temperature?: number | null;
    spo2?: number | null; respiratoryRate?: number | null; weight?: number | null } | null;
  /** Rich text (Markdown) — render with RichText. */
  notes?: string | null;
  prescription?: {
    diagnosis?: string | null;
    medicines: { name: string; dosage?: string | null; frequency?: string | null; duration?: string | null; instructions?: string | null }[];
    advice?: string | null;
    followUpDays?: number | null;
    followUpInstructions?: string | null;
  } | null;
  labTests: { testName: string; status?: string | null; abnormal: boolean; criticalNote?: string | null;
    abnormalValues: { parameter?: string | null; value?: string | null; unit?: string | null; severity?: string | null; critical: boolean }[] }[];
  followUp?: { date?: string | null; status?: string | null; instructions?: string | null } | null;
  /** Visits before this one at the clinic. */
  earlierVisits: number;
}

export interface PatientOverviewResponse {
  safetyAlerts: PatientOverviewSafetyAlert[];
  activeConditions: string[];
  vitalsTrend: PatientOverviewVitalReading[];
  currentMedications: PatientOverviewCurrentMedication[];
  recentVisits: PatientOverviewVisit[];
  recentPrescriptions: PatientOverviewPrescription[];
  recentLabOrders: PatientOverviewLabOrder[];
  followUpSummary: PatientOverviewFollowUpSummary;
  /** Past medical history accepted into the chart (check-in); null without medical-records access (medopd-docs 132). */
  surgicalHistory?: string | null;
  familyHistory?: string | null;
}

export interface MedicationSnapshot {
  name: string;
  dosage?: string;
  frequency?: string;
  duration?: string;
}

export interface VitalsSnapshot {
  systolicBP?: number;
  diastolicBP?: number;
  pulse?: number;
  temperature?: number;
  spO2?: number;
  weight?: number;
  respiratoryRate?: number;
  date?: string;
}

export interface PatientClinicalSnapshot {
  allergies?: string;
  bloodGroup?: string;
  chronicConditions?: string;
  lastDiagnosis?: string;
  chiefComplaint?: string;
  clinicalNotes?: string;
  prescriptionDate?: string;
  medications?: MedicationSnapshot[];
  lastVitals?: VitalsSnapshot;
}

export interface VisitLabOrder {
  id: string;
  testName: string;
  status: string;
  accessionId?: string | null;
  prescriptionId?: string | null;
  createdAt?: string | null;
  removable: boolean;
}

export const patientsModule = {
  /** Paginated patient directory — used by PatientSearch. */
  list: (params?: PatientListParams) =>
    fetchApi<PatientPagedResponse>(`/api/patients${buildQS(params)}`),

  /** Legacy full-list used by appointment booking and other selects. */
  getAll: async () => {
    const res = await fetchApi<PatientPagedResponse>('/api/patients?size=1000');
    if (res.error) return { error: res.error };
    return { data: (res.data?.content ?? []) as Patient[] };
  },

  search: (query: string) =>
    fetchApi<Patient[]>(`/api/patients/search?q=${encodeURIComponent(query)}`),

  searchByMobile: async (mobile: string) => {
    const response = await fetchApi<Patient[]>(
      `/api/patients/search?q=${encodeURIComponent(mobile)}`
    );
    return response.data?.find(p => p.mobile === mobile) ?? null;
  },

  getById: (id: string) =>
    fetchApi<Patient>(`/api/patients/${id}`),

  getByIds: (ids: string[]) =>
    fetchApi<Patient[]>('/api/patients/batch', {
      method: 'POST',
      body: JSON.stringify({ ids }),
    }),

  /** Bulk outstanding-balance lookup (patientId -> amount due) — one round trip for a whole
   *  search-results list instead of N+1 per-patient calls. */
  getBalances: (ids: string[]) =>
    fetchApi<Record<string, number>>(
      `/api/patients/balances?ids=${ids.map(encodeURIComponent).join(',')}`
    ),

  // sourcePatientId links a locally-registered record to a cross-branch/transferred source patient
  // (server authorizes the read + links their global identity + records provenance). See PatientPickerSection.
  /** confirmDuplicate: staff checked the records already on this number and this is a different person. */
  create: (patientData: Partial<Patient> & { sourcePatientId?: string; confirmDuplicate?: boolean }) =>
    fetchApi<Patient>('/api/patients', {
      method: 'POST',
      body: JSON.stringify(patientData),
    }),

  update: (id: string, patientData: Partial<Patient>) =>
    fetchApi<Patient>(`/api/patients/${id}`, {
      method: 'PUT',
      body: JSON.stringify(patientData),
    }),

  invite: (id: string, data: { email: string; redirectUrl: string }) =>
    fetchApi<{ invitationId: string; email: string; expiresAt: string }>(
      `/api/patients/${id}/invite`,
      { method: 'POST', body: JSON.stringify(data) }
    ),

  getLabOrders: (patientId: string) =>
    fetchApi<any[]>(`/api/patients/${patientId}/lab-orders`),

  /**
   * Order lab tests directly, with no prescription.
   *
   * Every test in one call lands on a single accession, so the lab receives one requisition and the
   * clinician sees one record. Ordering tests one at a time would create unrelated requisitions.
   *
   * `labId` is optional — omitted, the backend routes to the clinic's in-house lab.
   */
  createLabOrders: (patientId: string, body: { tests: string[]; labId?: string; instructions?: string; appointmentId?: string }) =>
    fetchApi<{ count: number; accessionId: string; orderIds: string[] }>(
      `/api/patients/${patientId}/lab-orders`,
      { method: 'POST', body: JSON.stringify(body) },
    ),

  /** Test names already ordered (and not cancelled) during this appointment — lets the ordering
   *  screen flag/block re-selecting the same test before the doctor even tries to submit. */
  getAlreadyOrderedTestsForAppointment: (patientId: string, appointmentId: string) =>
    fetchApi<string[]>(`/api/patients/${patientId}/lab-orders/appointment/${appointmentId}/existing-tests`),

  /** Lab orders raised during this appointment (lab drawer + the visit's prescription), newest first.
   *  `removable`: ordered from the drawer and not yet collected — prescription tests are edited
   *  through the prescription so the two never disagree. */
  getLabOrdersForAppointment: (patientId: string, appointmentId: string) =>
    fetchApi<VisitLabOrder[]>(`/api/patients/${patientId}/lab-orders/appointment/${appointmentId}`),

  /** Cancel one lab order (server refuses once results exist). */
  cancelLabOrder: (orderId: string, reason: string) =>
    fetchApi<unknown>(`/api/lab-orders/${orderId}/cancel`, { method: 'POST', body: JSON.stringify({ reason }) }),

  getVisits: (patientId: string) =>
    fetchApi<Visit[]>(`/api/patients/${patientId}/visits`),

  /** One page of the visit history, newest first, with each visit's doctorName (medopd-docs 117). */
  getVisitsPage: (patientId: string, page: number, size: number) =>
    fetchApi<PagedResponse<Visit>>(`/api/patients/${encodeURIComponent(patientId)}/visits${buildQS({ page, size })}`),

  /** One page of the patient's lab orders (same rows as getLabOrders), newest first (medopd-docs 117). */
  getLabOrdersPage: (patientId: string, page: number, size: number) =>
    fetchApi<PagedResponse<PatientLabOrderRow>>(`/api/patients/${encodeURIComponent(patientId)}/lab-orders${buildQS({ page, size })}`),

  /**
   * The workspace's full Lab Orders / Imaging list: the overview's lab-order rows, every requisition. Pages count
   * requisitions (a panel is never split); `content` holds every test of the requisitions on the page.
   */
  getLabRequisitionsPage: (patientId: string, params: { imaging?: boolean; page: number; size: number }) =>
    fetchApi<PagedResponse<PatientOverviewLabOrder>>(
      `/api/patients/${encodeURIComponent(patientId)}/lab-requisitions${buildQS(params)}`),

  getOverview: (patientId: string) =>
    fetchApi<{ data: PatientOverviewResponse }>(`/api/patients/${patientId}/overview`),

  /** The previous visit's summary for the doctor opening an appointment (doctors only); null on a first visit. */
  lastVisit: (patientId: string, appointmentId?: string) =>
    fetchApi<LastVisitSummary | null>(`/api/patients/${encodeURIComponent(patientId)}/last-visit${appointmentId ? `?appointmentId=${encodeURIComponent(appointmentId)}` : ''}`),

  /** General clinical snapshot (allergies, meds, last diagnosis/vitals) — not itself a referral/
   *  transfer concept, just consumed by the "Clinical Summary" step of that workflow. */
  getClinicalSnapshot: (patientId: string) =>
    fetchApi<PatientClinicalSnapshot>(`/api/patients/${patientId}/clinical-snapshot`),
};
